import { useEffect, useRef } from "react";

const Globe = ({ size = 560, className = "" }) => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.scale(dpr, dpr);

    const R = size * 0.4;
    const cx = size / 2;
    const cy = size / 2;

    let seed = 42;
    const rnd = () => {
      seed = (seed * 16807) % 2147483647;
      return seed / 2147483647;
    };
    const pts = [];
    for (let i = 0; i < 24; i++) {
      pts.push({
        lat: (rnd() * 130 - 65) * (Math.PI / 180),
        lng: rnd() * Math.PI * 2,
        bad: rnd() < 0.22,
        phase: rnd() * Math.PI * 2,
      });
    }
    const pairs = [];
    for (let i = 0; i < 9; i++) pairs.push([i, (i * 7 + 5) % pts.length]);

    let rot = 0;
    let t = 0;
    let raf;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const project = (lat, lng) => {
      const x0 = Math.cos(lat) * Math.sin(lng + rot);
      const y0 = Math.sin(lat);
      const z0 = Math.cos(lat) * Math.cos(lng + rot);
      return { x: cx + R * x0, y: cy - R * y0, z: z0 };
    };

    const strokePath = (points) => {
      ctx.beginPath();
      let started = false;
      for (const p of points) {
        if (p.z > -0.02) {
          if (!started) {
            ctx.moveTo(p.x, p.y);
            started = true;
          } else ctx.lineTo(p.x, p.y);
        } else started = false;
      }
      ctx.stroke();
    };

    const draw = () => {
      t += 0.016;
      if (!reduced) rot += 0.0016;
      ctx.clearRect(0, 0, size, size);

      ctx.strokeStyle = "rgba(10,10,10,0.35)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.stroke();

      ctx.strokeStyle = "rgba(10,10,10,0.07)";
      for (let la = -60; la <= 60; la += 20) {
        const lat = (la * Math.PI) / 180;
        const path = [];
        for (let a = 0; a <= Math.PI * 2 + 0.01; a += 0.06) path.push(project(lat, a));
        strokePath(path);
      }
      for (let lo = 0; lo < 360; lo += 20) {
        const lng = (lo * Math.PI) / 180;
        const path = [];
        for (let a = -Math.PI / 2; a <= Math.PI / 2 + 0.01; a += 0.05) path.push(project(a, lng));
        strokePath(path);
      }

      ctx.strokeStyle = "rgba(10,10,10,0.12)";
      ctx.beginPath();
      ctx.moveTo(cx - R - 18, cy);
      ctx.lineTo(cx + R + 18, cy);
      ctx.moveTo(cx, cy - R - 18);
      ctx.lineTo(cx, cy + R + 18);
      ctx.stroke();

      ctx.setLineDash([3, 6]);
      ctx.lineDashOffset = -t * 14;
      for (const [ai, bi] of pairs) {
        const A = project(pts[ai].lat, pts[ai].lng);
        const B = project(pts[bi].lat, pts[bi].lng);
        if (A.z > 0.05 && B.z > 0.05) {
          const mx = (A.x + B.x) / 2;
          const my = (A.y + B.y) / 2;
          const dx = mx - cx;
          const dy = my - cy;
          const dist = Math.hypot(dx, dy) || 1;
          const lift = 42;
          ctx.strokeStyle = "rgba(0,85,255,0.35)";
          ctx.beginPath();
          ctx.moveTo(A.x, A.y);
          ctx.quadraticCurveTo(mx + (dx / dist) * lift, my + (dy / dist) * lift, B.x, B.y);
          ctx.stroke();
        }
      }
      ctx.setLineDash([]);

      for (const p of pts) {
        const pr = project(p.lat, p.lng);
        if (pr.z <= 0) continue;
        const pulse = 1.6 + Math.sin(t * 2 + p.phase) * 0.9;
        ctx.fillStyle = p.bad ? "#FF3B30" : "#00A651";
        ctx.beginPath();
        ctx.arc(pr.x, pr.y, Math.max(1.2, pulse), 0, Math.PI * 2);
        ctx.fill();
        if (p.bad) {
          ctx.strokeStyle = "rgba(255,59,48,0.5)";
          ctx.beginPath();
          ctx.arc(pr.x, pr.y, 5 + ((t * 10 + p.phase * 8) % 10), 0, Math.PI * 2);
          ctx.stroke();
        }
      }

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [size]);

  return (
    <canvas
      ref={ref}
      data-testid="hero-globe"
      className={className}
      style={{ width: size, height: size, maxWidth: "100%" }}
      aria-label="Rotating wireframe globe showing verified and flagged delivery pings"
      role="img"
    />
  );
};

export default Globe;
